import { Tabs, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { settingsLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useCurrentAdminPermissions } from "@/hooks/useCurrentAdminPermissions";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  Settings,
  User,
  Wallet,
  Truck,
  FileText,
  Users,
  Building2,
  Newspaper,
  BookOpen,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { NativeSlideInPanel } from "@/components/native/NativeSlideInPanel";

const LazyGeneralSection = lazy(() =>
  import("./GeneralSection").then((m) => ({ default: m.GeneralSection })),
);
const LazyProfileSection = lazy(() =>
  import("./ProfileSection").then((m) => ({ default: m.ProfileSection })),
);
const LazyEconomySection = lazy(() =>
  import("./EconomySection").then((m) => ({ default: m.EconomySection })),
);
const LazyDeliverySection = lazy(() =>
  import("./profile/DeliverySection").then((m) => ({
    default: m.DeliverySection,
  })),
);
const LazyLegalPoliciesSection = lazy(() =>
  import("./LegalPoliciesSection").then((m) => ({
    default: m.LegalPoliciesSection,
  })),
);
const LazyNewsManagement = lazy(() => import("@/admin/settings/NewsManagement"));
const LazyGuidesManagement = lazy(() =>
  import("@/admin/settings/GuidesManagement"),
);
const LazyCustomerManagement = lazy(() =>
  import("@/admin/settings/CustomerManagement"),
);
const LazyCompanyManagement = lazy(() =>
  import("@/admin/settings/company/CompanyManagement").then((m) => ({
    default: m.CompanyManagement,
  })),
);
const SETTINGS_TAB_ICONS: Record<SettingsTabId, LucideIcon> = {
  general: Settings,
  profile: User,
  economy: Wallet,
  delivery: Truck,
  legal: FileText,
  customers: Users,
  companies: Building2,
  news: Newspaper,
  guides: BookOpen,
};

interface SettingsTabsProps {
  defaultTab?: string;
  pendingRequestsCount?: number;
}

type SettingsTabId =
  | "general"
  | "profile"
  | "economy"
  | "delivery"
  | "legal"
  | "customers"
  | "companies"
  | "news"
  | "guides";

export const SettingsTabs = ({
  defaultTab = "general",
  pendingRequestsCount: propPendingRequestsCount,
}: SettingsTabsProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view");
  const tabFromUrl = searchParams.get("tab");

  const { language } = useLanguage();
  const { isNative } = useNativePlatform();
  const labels = settingsLabels(language);
  const { canManageSettings } = useCurrentAdminPermissions();

  const resolveTab = (tab: string | null | undefined): SettingsTabId | null => {
    if (!tab || tab === "auction") return null;
    return tab as SettingsTabId;
  };

  const [activeTab, setActiveTab] = useState<SettingsTabId | null>(() =>
    resolveTab(tabFromUrl ?? (defaultTab || null)),
  );

  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(resolveTab(tabFromUrl));
    } else if (!defaultTab) {
      setActiveTab(null);
    }
  }, [tabFromUrl, defaultTab]);

  // Deep-link to news/guides without permission → bounce to settings hub
  useEffect(() => {
    if (
      !canManageSettings &&
      (activeTab === "news" || activeTab === "guides")
    ) {
      setActiveTab(null);
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  }, [canManageSettings, activeTab, searchParams, setSearchParams]);

  const isAdmin = user?.type === UserType.Admin;
  const cachedStats = queryClient.getQueryData([
    "registrationApplicationStats",
  ]) as { pending?: { total?: number } } | undefined;
  const pendingRequestsCount =
    propPendingRequestsCount !== undefined
      ? propPendingRequestsCount
      : isAdmin
        ? cachedStats?.pending?.total || 0
        : 0;

  const categories = useMemo(() => {
    if (!user) return [];
    const desc = labels.tabDescriptions as Record<string, string> | undefined;
    const withDesc = (id: SettingsTabId, label: string, badge?: number) => ({
      id,
      label,
      description: desc?.[id],
      badge,
    });

    const items: {
      id: SettingsTabId;
      label: string;
      description?: string;
      badge?: number;
    }[] = [
      withDesc("general", labels.tabs.general),
      withDesc("profile", labels.tabs.profile),
    ];

    if (user.type !== UserType.Admin) {
      items.push(
        withDesc("economy", labels.tabs.economy),
        withDesc("delivery", labels.tabs.delivery),
      );
    }

    if (isNative) {
      items.push(withDesc("legal", labels.tabs.legal));
    }

    if (user.type === UserType.Admin) {
      items.push(
        withDesc("customers", labels.tabs.users, pendingRequestsCount),
        withDesc("companies", labels.tabs.companies),
      );
      if (canManageSettings) {
        items.push(
          withDesc("news", labels.tabs.news),
          withDesc("guides", labels.tabs.guides),
        );
      }
    }

    return items;
  }, [user, labels, pendingRequestsCount, isNative, canManageSettings]);

  if (!user) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-muted-foreground">
          {labels.userManagement.loadingUserInfo}
        </p>
      </div>
    );
  }

  const activeLabel =
    categories.find((c) => c.id === activeTab)?.label ?? labels.title;

  const openTab = (id: SettingsTabId) => {
    setActiveTab(id);
    const next = new URLSearchParams(searchParams);
    next.set("tab", id);
    if (id !== "customers") {
      next.delete("view");
    }
    setSearchParams(next, { replace: true });
  };

  const closeTab = () => {
    setActiveTab(null);
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    next.delete("view");
    setSearchParams(next, { replace: true });
  };

  if (activeTab === null) {
    return (
      <div className="space-y-2 w-full">
        <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden bg-card">
          {categories.map((cat) => {
            const Icon = SETTINGS_TAB_ICONS[cat.id];
            return (
            <li key={cat.id}>
              <button
                type="button"
                className={cn(
                  "flex w-full items-center justify-between gap-3 px-4 text-left hover:bg-muted/50 transition-colors",
                  isNative ? "py-4 native-settings-row" : "py-3.5",
                )}
                onClick={() => openTab(cat.id)}
              >
                <span className="flex items-start gap-3 min-w-0 flex-1">
                  <Icon className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" aria-hidden />
                <span className="flex flex-col items-start gap-0.5 min-w-0 pr-2">
                  <span className="font-medium text-foreground">{cat.label}</span>
                  {cat.description ? (
                    <span className="text-xs text-muted-foreground leading-snug line-clamp-2">
                      {cat.description}
                    </span>
                  ) : null}
                </span>
                {cat.badge != null && cat.badge > 0 ? (
                  <span className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center shrink-0">
                    {cat.badge > 9 ? "9+" : cat.badge}
                  </span>
                ) : null}
                </span>
              </button>
            </li>
          );
          })}
        </ul>
      </div>
    );
  }

  const sectionFallback = (
    <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
      {labels.userManagement.loadingUserInfo}
    </div>
  );

  const renderSection = () => {
    switch (activeTab) {
      case "general":
        return <LazyGeneralSection />;
      case "profile":
        return <LazyProfileSection />;
      case "economy":
        return <LazyEconomySection />;
      case "delivery":
        return <LazyDeliverySection />;
      case "legal":
        return <LazyLegalPoliciesSection />;
      case "news":
        return <LazyNewsManagement />;
      case "guides":
        return <LazyGuidesManagement />;
      case "customers":
        return (
          <LazyCustomerManagement
            pendingRequestsCount={pendingRequestsCount}
            defaultView={viewParam || undefined}
          />
        );
      case "companies":
        return <LazyCompanyManagement />;
      default:
        return null;
    }
  };

  if (isNative) {
    return (
      <NativeSlideInPanel always variant="slide" className="native-settings-detail-panel w-full">
        <Button
          type="button"
          variant="ghost"
          className="w-full justify-start gap-2 px-1 h-10 font-semibold -ml-0.5"
          onClick={closeTab}
        >
          <ChevronLeft className="h-5 w-5" />
          {activeLabel}
        </Button>
        <div className="native-settings-detail-body mt-2">
          <Suspense fallback={sectionFallback}>{renderSection()}</Suspense>
        </div>
      </NativeSlideInPanel>
    );
  }

  return (
    <Tabs value={activeTab} className="space-y-3 w-full">
      <Button
        type="button"
        variant="ghost"
        className="w-full justify-start gap-2 px-1 h-10 font-semibold"
        onClick={closeTab}
      >
        <ChevronLeft className="h-5 w-5" />
        {activeLabel}
      </Button>

      <TabsContent value="general">
        <Suspense fallback={sectionFallback}>
          <LazyGeneralSection />
        </Suspense>
      </TabsContent>

      <TabsContent value="profile">
        <Suspense fallback={sectionFallback}>
          <LazyProfileSection />
        </Suspense>
      </TabsContent>

      <TabsContent value="economy">
        <Suspense fallback={sectionFallback}>
          <LazyEconomySection />
        </Suspense>
      </TabsContent>

      <TabsContent value="delivery">
        <Suspense fallback={sectionFallback}>
          <LazyDeliverySection />
        </Suspense>
      </TabsContent>

      <TabsContent value="news">
        <Suspense fallback={sectionFallback}>
          <LazyNewsManagement />
        </Suspense>
      </TabsContent>

      <TabsContent value="guides">
        <Suspense fallback={sectionFallback}>
          <LazyGuidesManagement />
        </Suspense>
      </TabsContent>

      <TabsContent value="customers">
        <Suspense fallback={sectionFallback}>
          <LazyCustomerManagement
            pendingRequestsCount={pendingRequestsCount}
            defaultView={viewParam || undefined}
          />
        </Suspense>
      </TabsContent>

      <TabsContent value="companies">
        <Suspense fallback={sectionFallback}>
          <LazyCompanyManagement />
        </Suspense>
      </TabsContent>

      <TabsContent value="legal">
        <Suspense fallback={sectionFallback}>
          <LazyLegalPoliciesSection />
        </Suspense>
      </TabsContent>
    </Tabs>
  );
};
